import type { TranslationView } from "@/v2-backoffice-client/translation/translation.constants"
import type { TranslationCategory } from "@/v2-backoffice-client/translation/translation.types"

// The contextual help that used to sit as a paragraph above each table.
// Rendered as an info tooltip on the sidebar item so the explanation is
// available from anywhere without eating table space.
export type SidebarInfo = {
  title: string
  body: string
}

export type PinnedItem = {
  view: TranslationView
  label: string
  featureCode: string
  info: SidebarInfo
  count: number | null
  // True while count is still resolving (distinct from count === null,
  // which Duplicates/Add type values/Languages also use permanently to mean
  // "no count applies here").
  loading: boolean
  // Dot signal: green when there is nothing to do, amber when there is.
  attention: boolean
}

// The sidebar's pinned groups, widest first: "Catalog" is the whole
// inventory, "Text" is field-level work on records that already exist (a
// language value is missing or wrong), "Code ↔ database" is record-level
// drift between the code catalog (*.text.ts) and the database (a whole text
// exists on one side only, or its English differs) — kept in sync by
// automation, so those views are mostly observational — and "Tools" adds
// new records rather than working on existing ones.
export type PinnedSection = {
  label: string
  info: SidebarInfo
  items: PinnedItem[]
}

// Collapse state survives reloads: with ~30 UI + ~50 data categories the
// sidebar is long, and an operator working only on data labels shouldn't
// have to re-collapse App UI every visit.
const COLLAPSED_SECTIONS_STORAGE_KEY = "translation-sidebar-collapsed-sections"

export type CollapsedSections = Partial<
  Record<TranslationCategory["section"], boolean>
>

export function readCollapsedSections(): CollapsedSections {
  try {
    return JSON.parse(
      localStorage.getItem(COLLAPSED_SECTIONS_STORAGE_KEY) ?? "{}",
    )
  } catch {
    return {}
  }
}

export function writeCollapsedSections(value: CollapsedSections) {
  localStorage.setItem(COLLAPSED_SECTIONS_STORAGE_KEY, JSON.stringify(value))
}

export function matchesCategoryQuery(
  category: TranslationCategory,
  query: string,
) {
  return (
    category.label.toLowerCase().includes(query) ||
    category.key.toLowerCase().includes(query)
  )
}

export type BuildPinnedSectionsInput = {
  categoriesLoading: boolean
  totalRowCount: number
  incompleteRowCount: number
  // Counts from the (cheap, cached) drift queries; null while loading.
  missingInCodeCount: number | null
  orphanedCount: number | null
  fallbackMismatchCount: number | null
  // Changes since the last successful publish; null while loading or when
  // no publish baseline exists yet.
  pendingPublishCount: number | null
}

export function buildPinnedSections(
  input: BuildPinnedSectionsInput,
): PinnedSection[] {
  const {
    categoriesLoading,
    totalRowCount,
    incompleteRowCount,
    missingInCodeCount,
    orphanedCount,
    fallbackMismatchCount,
    pendingPublishCount,
  } = input

  return [
    {
      label: "Catalog",
      info: {
        title: "Catalog",
        body: "Everything the apps read out of v2-features-db: features (app UI, nav layers, basemaps, map layers, 3D styles…) and the data labels behind enum columns. This is the whole inventory; the sections below are work queues cut from it.",
      },
      items: [
        {
          view: "catalog",
          label: "All entries",
          featureCode: "UI_BackofficeTranslation_Sidebar_All",
          info: {
            title: "All entries",
            body: "Every entry in the catalog — features and data labels (type values, e.g. industry classifications) alike, searchable across code and all languages. Click a feature row for its three facets: Text (per language, ships with the next publish), Configuration and Availability (both live immediately). A data label has text only.",
          },
          count: categoriesLoading ? null : totalRowCount,
          loading: categoriesLoading,
          attention: false,
        },
      ],
    },
    {
      label: "Text",
      info: {
        title: "Text",
        body: "The translation work queues: what a feature or data label SAYS, per language. Edits are saved to the catalog immediately, and reach the apps with the next publish — unlike a row's Configuration and Availability, which go live the moment they are saved.",
      },
      items: [
        {
          view: "incomplete",
          label: "Missing translations",
          featureCode: "UI_BackofficeTranslation_Sidebar_Incomplete",
          info: {
            title: "Missing translations",
            body: "Texts (UI texts and data labels alike) that have a database record but no value in one or more supported languages — the translation work queue. Open a row, write or AI-suggest the missing languages, and save.",
          },
          count: categoriesLoading ? null : incompleteRowCount,
          loading: categoriesLoading,
          attention: incompleteRowCount > 0,
        },
        {
          view: "duplicates",
          label: "Duplicates",
          featureCode: "UI_BackofficeTranslation_Sidebar_Duplicates",
          info: {
            title: "Duplicates",
            body: "Groups of texts that are spelled almost the same (fuzzy match, computed locally) or mean the same (AI scan, run on demand). Resolve a group by linking it under one master so editing one updates all — non-destructive.",
          },
          count: null,
          loading: false,
          attention: false,
        },
        {
          view: "pending",
          label: "Review pending",
          featureCode: "UI_BackofficeTranslation_Sidebar_PendingPublish",
          info: {
            title: "Review pending",
            body: "Everything the next publish will ship, with a git-style diff against the published value: text, configuration, availability, personal data and countries. Expand a row to open the facet that changed. Text lives only in the catalog, so it can be adjusted or reverted here; the rest was saved straight to this environment's runtime and is already live — it appears so you can see what a publish carries to the other environments.",
          },
          count: pendingPublishCount,
          loading: pendingPublishCount === null,
          attention: (pendingPublishCount ?? 0) > 0,
        },
      ],
    },
    {
      label: "Code ↔ database",
      info: {
        title: "Code ↔ database",
        body: "Whole texts drifting between the code catalog (*.text.ts) and the database — a record exists on one side only, or its English differs. The Translation Text Sync workflow reconciles these automatically on every merge to master (and daily); these views show what is queued.",
      },
      items: [
        {
          view: "missing",
          label: "Missing in database",
          featureCode: "UI_BackofficeTranslation_Sidebar_MissingInDatabase",
          info: {
            title: "Missing in database",
            body: "New texts that ship in the code but have no database record yet. The app renders their English fallback and they cannot be translated until promoted. Select rows in the table to promote them: 'Promote' seeds the English text and queues them under Missing translations; 'Promote + AI translate' also drafts every language. Mirror image of Missing in code.",
          },
          count: missingInCodeCount,
          loading: missingInCodeCount === null,
          attention: (missingInCodeCount ?? 0) > 0,
        },
        {
          view: "orphaned",
          label: "Missing in code",
          featureCode: "UI_BackofficeTranslation_Sidebar_MissingInCode",
          info: {
            title: "Missing in code",
            body: "Database records whose text no longer exists in the code — its usage was removed or renamed. Select rows in the table to soft-delete them (history is kept, restorable), or re-wire the code if the removal was accidental. Mirror image of Missing in database.",
          },
          count: orphanedCount,
          loading: orphanedCount === null,
          attention: (orphanedCount ?? 0) > 0,
        },
        {
          view: "fallback",
          label: "Pending code sync",
          featureCode: "UI_BackofficeTranslation_Sidebar_Fallback",
          info: {
            title: "Pending code sync",
            body: "Texts whose English value in the database differs from the code fallback. Nothing to do: the database wins, and the sync workflow commits these values into the code on the next merge to master (or the daily run). The 'Differs in' column shows which field diverges — identical-looking rows usually differ in the description or in invisible characters.",
          },
          count: fallbackMismatchCount,
          loading: fallbackMismatchCount === null,
          attention: (fallbackMismatchCount ?? 0) > 0,
        },
      ],
    },
    {
      label: "Tools",
      info: {
        title: "Tools",
        body: "One-off workflows that add new records to the catalog, as opposed to the views above that work on what is already there.",
      },
      items: [
        {
          view: "add-objects",
          label: "Add type values",
          featureCode: "UI_BackofficeTranslation_Sidebar_AddObjects",
          info: {
            title: "Add type values",
            body: "Bulk-add labels for DATA values (common.TypeValues): the possible values of a database column, e.g. ownership types or building statuses. Paste a table of codes + names, let the AI write every supported language, review, and save. UI texts (common.Features) never enter here — they flow in from the code automatically.",
          },
          count: null,
          loading: false,
          attention: false,
        },
      ],
    },
    {
      label: "Registries",
      info: {
        title: "Registries",
        body: "The two small controlled vocabularies the rest of the platform is measured against: which languages the catalog is written in, and which countries it is sold in. Each has one flag that turns capability on everywhere, so these are the widest-reaching switches on the page — and both are authored here and go live with the next publish.",
      },
      items: [
        {
          view: "languages",
          label: "Languages",
          featureCode: "UI_BackofficeTranslation_Sidebar_Languages",
          info: {
            title: "Languages",
            body: "The language registry with per-language coverage. Enabling a language makes it a translation target everywhere (editor fields, AI fills, publish exports); the user-facing selector stays behind the PostHog staff gate. Languages without a STYLE.md section warn before enabling — AI fills need an orthography authority on record.",
          },
          count: null,
          loading: false,
          attention: false,
        },
        {
          view: "countries",
          label: "Countries",
          featureCode: "UI_BackofficeTranslation_Sidebar_Countries",
          info: {
            title: "Countries",
            body: "The country registry and the market switch behind it. Supporting a country is what puts it in the console's project picker, in a user's selectable context, and in the country availability list of every feature, basemap and nav layer. The catalog already holds the full ISO 3166 list, so opening a market is a flag on an existing row — the readiness column says what is still missing (map geometry, names). Expand a row to edit its names per language; those live on the country itself, not in the translation catalog, so the translation queues never surface them.",
          },
          count: null,
          loading: false,
          attention: false,
        },
      ],
    },
  ]
}
